import React, {FC} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {onlyItem} from '../../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {KeyboardShortcuts} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {TimeFormatsEnum} from '../../../../../helpers/dateTimeHelpers';
import {handlersWithTextInputIntent} from '../../../../../helpers/keyboardHandlerHelpers';
import {ReplyTypesEnum} from '../../../../../helpers/models/recipientHelpers';
import {ViewerDropdownInterfaceEventSourcesEnum} from '../../../../../helpers/models/tagHelpers';
import {areConversationSafetyNetsSatisfied} from '../../../../../helpers/safetyNetsHelpers';
import {ConversationBaseModel, ConversationModel} from '../../../../../models/api/conversationModel';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {SafetyNetsEnum} from '../../../../../models/api/safetyNetModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {ReplyLocationsEnum} from '../../../../../services/tracking';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {PermissionToolbarButton} from '../../../../common/buttons/permissionToolbarButton';
import {
  ConversationsTagButtonProps,
  ConversationsTagCoordinator,
} from '../actions/tag/conversationsTagCoordinator';
import {ConversationTopbarAssign} from './conversationTopbarAssign';
import {ConversationTopbarInviteContainer} from './conversationTopbarInviteContainer';
import {ConversationTopbarMore} from './conversationTopbarMore';
import {NewConversationTopbarMove} from './newConversationTopbarMove';

/*
 * Props.
 */

type ReplyHandler = (location: ReplyLocationsEnum, shouldPopout?: boolean) => void;
type CloneHandler = (location: ReplyLocationsEnum) => void;

interface NewConversationTopbarProps {
  panelId: ViewerPanelId;
  teammate: TeammateModel;
  onlyConversation: ConversationModel | undefined;
  conversations: ReadonlyArray<ConversationBaseModel>;
  contextNamespaces: ReadonlyArray<string>;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  assigneeForAll: TeammateModel | undefined;
  reminderForAll: ReminderModel | undefined;
  areAllSubscribed: boolean;
  areAllShared: boolean;
  areAllInTeamContext: boolean;
  shouldShowSubscribe: boolean;
  isInPopup: boolean;
  replyType: ReplyTypesEnum;
  timeFormat: TimeFormatsEnum;
  isBulkActionRequired: boolean;

  onReplyClick: ReplyHandler;
  onReplyAllClick: ReplyHandler;
  onReplyIntroClick: ReplyHandler;
  onForwardClick: ReplyHandler;
  onCloneClick: CloneHandler;
  onDeleteMessageClick: HandlerOf<string>;
  onToggleAssignToSelfClick: Handler;
  onPopinClick: Handler;
  onSubscribeClick: Handler;
  onUnsubscribeClick: Handler;
  shouldShowNewUserTooltip: boolean;
  onSubjectChange: HandlerOf<string>;
}

/*
 * Style.
 */

const StyledConversationTopbar = styled.div`
  grid-area: topbar;

  background-color: ${(p) => p.theme.appBackground};
`;

const StyledContentDiv = styled.div`
  grid-area: content;
  display: flex;
  align-items: center;
  padding: 8px 10px 0 8px;
  padding-left: 3px;
  padding-right: 14px;
`;

const StyledMiddleSpacer = styled.div`
  flex: 1;
`;

const StyledButtonDiv = styled.div``;

const StyledShrinkableButtonDiv = styled(StyledButtonDiv)`
  min-width: 0;
`;

const StyledButtonsDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-gap: 8px;
`;

const StyledTagButtonWrapperDiv = styled.div`
  position: relative;
`;

const StyledStarDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};

  position: absolute;
  top: 3px;
  right: -1px;
  font-size: ${fontSizes.large};
`;

/*
 * Component.
 */

export const NewConversationTopbar: FC<NewConversationTopbarProps> = (props) => {
  if (props.conversations.length === 0) {
    return null;
  }

  // Always bind keys for both actions.
  const keyHandlers = {
    ...handlersWithTextInputIntent({
      [KeyboardActionsEnum.REPLY]: () => {
        props.onReplyClick(ReplyLocationsEnum.KEYBOARD);
      },
      [KeyboardActionsEnum.REPLY_IN_NEW_WINDOW]: () => {
        props.onReplyClick(ReplyLocationsEnum.KEYBOARD, true);
      },
      [KeyboardActionsEnum.REPLY_ALL]: () => props.onReplyAllClick(ReplyLocationsEnum.KEYBOARD),
      [KeyboardActionsEnum.REPLY_ALL_IN_NEW_WINDOW]: () =>
        props.onReplyAllClick(ReplyLocationsEnum.KEYBOARD, true),
      [KeyboardActionsEnum.REPLY_INTRO]: () => props.onReplyIntroClick(ReplyLocationsEnum.KEYBOARD),
      [KeyboardActionsEnum.FORWARD]: () => props.onForwardClick(ReplyLocationsEnum.KEYBOARD),
      [KeyboardActionsEnum.CLONE]: () => props.onCloneClick(ReplyLocationsEnum.KEYBOARD),
    }),
    [KeyboardActionsEnum.ASSIGN_TO_SELF]: () => props.onToggleAssignToSelfClick(),
    [KeyboardActionsEnum.SUBSCRIBE]: () =>
      props.areAllSubscribed ? props.onUnsubscribeClick() : props.onSubscribeClick(),
  };

  const onlyConversation = onlyItem(props.conversations);

  const maybeRenderRequiredTaggingStar = () => {
    if (
      !onlyConversation ||
      areConversationSafetyNetsSatisfied(onlyConversation, SafetyNetsEnum.REQUIRED_TAGGING)
    ) {
      return null;
    }

    return <StyledStarDiv>*</StyledStarDiv>;
  };

  return (
    <StyledConversationTopbar>
      <KeyboardShortcuts handlers={keyHandlers} />
      <StyledContentDiv>
        <StyledButtonDiv>
          <NewConversationTopbarMove
            cellsContext={props.cellsContext}
            panelId={props.panelId}
            conversations={props.conversations}
            inboxesParameters={props.inboxesParameters}
            isBulkActionRequired={props.isBulkActionRequired}
          />
        </StyledButtonDiv>
        <StyledMiddleSpacer />
        <StyledButtonsDiv>
          <ConversationTopbarMore
            cellsContext={props.cellsContext}
            inboxesParameters={props.inboxesParameters}
            onlyConversation={props.onlyConversation}
            conversations={props.conversations}
            isBulkActionRequired={props.isBulkActionRequired}
            panelId={props.panelId}
          />
          <StyledTagButtonWrapperDiv>
            <ConversationsTagCoordinator
              cellsContext={props.cellsContext}
              inboxesParameters={props.inboxesParameters}
              onlyConversation={props.onlyConversation}
              conversations={props.conversations}
              isBulkActionRequired={props.isBulkActionRequired}
              panelId={props.panelId}
              renderButton={(tagProps) => renderAddTagsButton(tagProps)}
              interfaceEventSource={ViewerDropdownInterfaceEventSourcesEnum.TOPBAR}
            />
            {maybeRenderRequiredTaggingStar()}
          </StyledTagButtonWrapperDiv>
          {renderInviteMenu(props)}
          {renderAssign(props)}
        </StyledButtonsDiv>
      </StyledContentDiv>
    </StyledConversationTopbar>
  );
};

/*
 * Helpers.
 */

function renderAddTagsButton(props: ConversationsTagButtonProps) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <PermissionToolbarButton
          iconName="tag"
          label={<Trans id="conversation-tags">Add tags</Trans>}
          hasHover={props.hasHover}
          keyboardAction={KeyboardActionsEnum.TAG}
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
          bulkActionPermission={props.permissionResult}
          noMargin
        />
      )}
    </UserThemeConsumer>
  );
}

/*
 * Invite.
 */

function renderInviteMenu(props: NewConversationTopbarProps) {
  const {onlyConversation} = props;

  if (!onlyConversation) {
    return null;
  }

  return (
    <ConversationTopbarInviteContainer cellsContext={props.cellsContext} conversation={onlyConversation} />
  );
}

/*
 * Assign.
 */

function renderAssign(props: NewConversationTopbarProps) {
  const StyledWrapper = props.assigneeForAll ? StyledShrinkableButtonDiv : StyledButtonDiv;

  return (
    <StyledWrapper>
      <ConversationTopbarAssign
        cellsContext={props.cellsContext}
        conversations={props.conversations}
        assigneeAll={props.assigneeForAll}
        panelId={props.panelId}
        inboxesParameters={props.inboxesParameters}
        isBulkActionRequired={props.isBulkActionRequired}
      />
    </StyledWrapper>
  );
}
